import { TaskMemberRole } from '@graphql/generated';

import type {
  StringSearchType,
  UserSearchType,
} from '@components/TaskList/TaskInviteOrAssignee.web';

export const isStringSearchType = (
  t: UserSearchType
): t is StringSearchType => {
  return t.__typename === 'StringSearchType';
};

export const getDisplayName = (t: UserSearchType): string => {
  if (isStringSearchType(t)) {
    return t.title;
  }
  const { name, firstName, lastName } = t;
  return name ?? `${firstName ?? ''} ${lastName ?? ''}`.trim();
};

export const getRoleDisplayString = (role: TaskMemberRole): string => {
  if (role === TaskMemberRole.Assignee) {
    return 'Assignee';
  } else if (role === TaskMemberRole.Viewer) {
    return 'Viewer';
  }
  return 'Owner';
};

export type TaskMemberLike = {
  role: TaskMemberRole;
  user: { id: string };
};

export type TaskMemberAttributesLike = {
  userId: string;
  role: TaskMemberRole;
};

/**
 * Builds the `attributes.members` payload for an `updateTask` call that
 * removes one or more members from a task.
 *
 * `updateTask` replaces the whole member set, so the payload must list every
 * member that should survive — including the OWNER and any member the UI is
 * not currently rendering as a chip.
 *
 * TA-10633: the previous inline version additionally kept only members that
 * appeared in the modal's visible chip list. The task OWNER is deliberately
 * filtered out of that list (TaskListItem drops `role === Owner`), so removing
 * a single assignee also deleted the owner membership. The server honoured the
 * payload and returned `members: []` / `userIds: []`, which cleared
 * `currentUserRole` in the cache — after that `canEditTaskTopLevel` was false,
 * so the role picker offered Viewer only and the user could not re-assign
 * until a refresh reloaded the task.
 */
export const buildMembersAfterRemoval = (
  members: readonly TaskMemberLike[] | null | undefined,
  // Callers build this from `UserSearchType.id`, which is optional — accept
  // the looser element type rather than forcing a cast at every call site.
  removedUserIds: Iterable<string | undefined>
): TaskMemberAttributesLike[] => {
  const removed = new Set(removedUserIds);

  return (members ?? [])
    .filter((member) => !removed.has(member.user.id))
    .map((member) => ({ userId: member.user.id, role: member.role }));
};
